import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Instagram, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import ProductCard from "@/components/ProductCard";
import HeroSlider from "@/components/HeroSlider";
import OptImage from "@/components/OptImage";

// SessionStorage cache keys — instantly restore CMS data on repeat visits
const CACHE_HERO = "uk-cms-hero-v1";
const CACHE_CARDS = "uk-cms-cards-v1";

const readCache = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) { return null; }
};

const writeCache = (key, val) => {
  try { sessionStorage.setItem(key, JSON.stringify(val || [])); } catch (e) { /* quota */ }
};

export default function Home() {
  const { t, lang } = useLang();
  const [featured, setFeatured] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hp, setHp] = useState(null);
  const [site, setSite] = useState(null);

  // Atomic hero state — { status: 'loading' | 'ready', slides: [...] }
  // status:'ready' is set ONLY together with slides → never an intermediate frame
  // where we'd flash the legacy hero before slides arrive.
  const [hero, setHero] = useState(() => {
    const cached = readCache(CACHE_HERO);
    return cached ? { status: "ready", slides: cached, fromCache: true } : { status: "loading", slides: [] };
  });
  const [cards, setCards] = useState(() => {
    const cached = readCache(CACHE_CARDS);
    return cached ? { status: "ready", items: cached, fromCache: true } : { status: "loading", items: [] };
  });

  useEffect(() => {
    // CMS data — atomic update, with sessionStorage caching for instant repeat-load.
    api.get("/homepage/hero-slides")
      .then((r) => {
        const slides = r.data || [];
        setHero({ status: "ready", slides });
        writeCache(CACHE_HERO, slides);
      })
      .catch(() => setHero((prev) => ({ status: "ready", slides: prev.slides || [] })));

    api.get("/homepage/category-cards")
      .then((r) => {
        const items = r.data || [];
        setCards({ status: "ready", items });
        writeCache(CACHE_CARDS, items);
      })
      .catch(() => setCards((prev) => ({ status: "ready", items: prev.items || [] })));

    // Non-blocking secondary data
    (async () => {
      try {
        const [f, n, b, cats, settings, siteData] = await Promise.all([
          api.get("/products?featured=true&limit=8"),
          api.get("/products?sort=newest&limit=4"),
          api.get("/products?limit=4"),
          api.get("/categories"),
          api.get("/settings/homepage"),
          api.get("/settings/site"),
        ]);
        setFeatured(f.data.items || []);
        setNewProducts(n.data.items || []);
        setBestSellers((b.data.items || []).filter((p) => p.is_bestseller).slice(0, 4));
        setCategories(cats.data || []);
        setHp(settings.data);
        setSite(siteData.data);
      } catch (e) { /* non-critical */ }
    })();
  }, []);

  // Preload the first slide image as soon as we know it (boosts LCP)
  useEffect(() => {
    if (!hero.slides.length) return;
    const first = hero.slides[0];
    if (!first?.image_url) return;
    const src = first.image_url;
    const isApiFile = typeof src === "string" && src.includes("/api/files/");
    const href = isApiFile ? `/api/img?u=${encodeURIComponent(src)}&fmt=webp` : src;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = href;
    if (isApiFile) link.type = "image/webp";
    link.setAttribute("data-hero-preload", "");
    if (isApiFile) {
      const enc = encodeURIComponent(src);
      link.setAttribute("imagesrcset",
        `/api/img?u=${enc}&w=1200&fmt=webp 1200w, /api/img?u=${enc}&w=1920&fmt=webp 1920w, /api/img?u=${enc}&w=2400&fmt=webp 2400w, /api/img?u=${enc}&fmt=webp 3000w`);
      link.setAttribute("imagesizes", "100vw");
    }
    document.head.appendChild(link);
    return () => { try { document.head.removeChild(link); } catch (e) { /* ignore */ } };
  }, [hero.slides]);

  // Live-update on admin save
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    if (!apiUrl) return;
    try {
      const wsUrl = apiUrl.replace(/^http/i, "ws") + "/api/ws";
      const ws = new WebSocket(wsUrl);
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg?.type === "homepage_updated") {
            api.get("/homepage/hero-slides").then((r) => {
              setHero({ status: "ready", slides: r.data || [] });
              writeCache(CACHE_HERO, r.data || []);
            });
            api.get("/homepage/category-cards").then((r) => {
              setCards({ status: "ready", items: r.data || [] });
              writeCache(CACHE_CARDS, r.data || []);
            });
          }
        } catch (e) { /* ignore */ }
      };
      return () => { try { ws.close(); } catch (e) { /* ignore */ } };
    } catch (e) { /* ignore */ }
  }, []);

  // Hero render — 3 distinct visual states, NEVER overlap:
  //   loading   → solid dark shell (matches slider bg, no pulse)
  //   slides>0  → HeroSlider (CMS)
  //   slides=0  → legacy hero (Unsplash + framer motion)
  const renderHero = () => {
    if (hero.status === "loading") {
      return (
        <section
          data-testid="hero-skeleton"
          className="relative w-full overflow-hidden bg-[#0A0A0A] aspect-[4/5] md:aspect-[16/7]"
          aria-hidden="true"
        />
      );
    }
    if (hero.slides.length > 0) {
      return <HeroSlider slides={hero.slides} interval={6000} />;
    }
    return (
      <section data-testid="hero-section" className="relative aspect-[4/5] md:aspect-[16/7] overflow-hidden grain bg-[#0A0A0A]">
        <img
          src={hp?.hero_image || "https://images.unsplash.com/photo-1573408301185-9146fe634ad0?auto=format&fit=crop&w=2400&q=85"}
          alt="Luxury jewelry"
          className="absolute inset-0 w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#EAF2EA]/15 via-[#222222]/40 to-[#222222]/70" />
        <div className="relative z-10 h-full flex items-end pb-24 px-6 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2 }} className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.4em] text-[#E5E4DF] mb-6">{lang === "tr" ? "Uzungöl Kuyumculuk · 2010" : "Uzungöl Jewelry · Est. 2010"}</p>
            <h1 data-testid="hero-title" className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[0.95] tracking-tight text-shadow-luxury">
              {lang === "tr" ? (hp?.hero_title_tr || "Zarafetin ve Geleneğin Buluştuğu Yer") : (hp?.hero_title_en || "Where Elegance Meets Tradition")}
            </h1>
            <p className="text-base text-[#E5E4DF] mt-6 max-w-lg leading-relaxed">
              {lang === "tr" ? (hp?.hero_subtitle_tr || "Üç nesildir Trabzon'da el işçiliği ile mücevher üretiyoruz.") : (hp?.hero_subtitle_en || "Three generations crafting fine jewelry in Trabzon.")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/products" data-testid="hero-shop-btn" className="bg-[#C8A96A] hover:bg-[#A6864A] text-white text-xs uppercase tracking-[0.25em] px-10 py-4 transition-colors inline-flex items-center gap-3">
                {t("home.shop")} <ArrowRight size={14} />
              </Link>
              <Link to="/categories" data-testid="hero-collections-btn" className="border border-white/80 text-white hover:bg-white hover:text-[#0A0A0A] text-xs uppercase tracking-[0.25em] px-10 py-4 transition-colors">
                {lang === "tr" ? "Koleksiyonlar" : "Collections"}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    );
  };

  return (
    <div data-testid="home-page">
      {renderHero()}

      {/* Category Cards Section — CMS-managed if configured, else legacy categories grid */}
      <section data-testid="categories-section" className="py-16 md:py-24 px-6 lg:px-16 max-w-[1440px] mx-auto">
        <div className="flex items-end justify-between mb-10 md:mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A96A] mb-3">{lang === "tr" ? "Koleksiyonlar" : "Collections"}</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl">{t("home.categories")}</h2>
          </div>
          <Link to="/categories" className="text-xs uppercase tracking-[0.25em] underline underline-offset-8 decoration-[#C8A96A]">
            {t("common.view_all")}
          </Link>
        </div>

        {cards.status === "loading" ? (
          <CardGridSkeleton />
        ) : cards.items.length > 0 ? (
          <CardGrid>
            {cards.items.map((c) => <CmsCard key={c.id} card={c} />)}
          </CardGrid>
        ) : (
          <CardGrid>
            {categories.slice(0, 6).map((c, i) => (
              <Link
                key={c.id}
                to={`/products?category=${c.id}`}
                data-testid={`category-card-${c.slug}`}
                className={"group block relative overflow-hidden bg-[#0A0A0A] " + (i === 0 ? "md:col-span-2 md:row-span-2 aspect-[4/5] md:aspect-auto" : "aspect-[4/5]")}
              >
                <CardImage src={c.image_url} alt={c.name_tr} />
                <CardOverlay
                  title={lang === "tr" ? c.name_tr : c.name_en}
                  subtitle={lang === "tr" ? "Keşfet" : "Discover"}
                />
              </Link>
            ))}
          </CardGrid>
        )}
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section data-testid="featured-section" className="py-16 md:py-24 px-6 lg:px-16 max-w-[1440px] mx-auto">
          <div className="flex items-end justify-between mb-10 md:mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C8A96A] mb-3">{lang === "tr" ? "Seçkin" : "Curated"}</p>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl">{t("home.featured")}</h2>
            </div>
            <Link to="/products" className="text-xs uppercase tracking-[0.25em] underline underline-offset-8 decoration-[#C8A96A]">{t("common.view_all")}</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6">
            {featured.slice(0, 10).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* New arrivals */}
      {newProducts.length > 0 && (
        <section data-testid="new-section" className="py-16 md:py-24 px-6 lg:px-16 max-w-[1440px] mx-auto">
          <div className="mb-10 md:mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A96A] mb-3">{lang === "tr" ? "Yenilikler" : "New"}</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl">{t("home.new")}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6">
            {newProducts.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Best sellers */}
      {bestSellers.length > 0 && (
        <section data-testid="bestsellers-section" className="py-16 md:py-24 px-6 lg:px-16 bg-[#F5F2EC]">
          <div className="max-w-[1440px] mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl mb-10 md:mb-12">{t("home.bestsellers")}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6">
              {bestSellers.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* About + Instagram strip + Map (legacy, unchanged) */}
      <section className="py-16 md:py-24 px-6 lg:px-16 max-w-[1440px] mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8A96A] mb-3">{lang === "tr" ? "Hakkımızda" : "About"}</p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl mb-6">{site?.site_name || "Uzungöl Kuyumculuk"}</h2>
          <p className="text-sm leading-relaxed text-[#52525B] whitespace-pre-line">
            {lang === "tr" ? (hp?.about_tr || "Çaykara, Trabzon'da köklü bir aile işletmesi.") : (hp?.about_en || "A family business rooted in Çaykara, Trabzon.")}
          </p>
          <Link to="/categories" className="mt-8 inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] underline underline-offset-8 decoration-[#C8A96A]">
            {lang === "tr" ? "Mağazayı Ziyaret Et" : "Visit the boutique"} <ArrowRight size={14} />
          </Link>
        </div>
        {site?.map_embed_src && (
          <a href={site?.map_directions_url} target="_blank" rel="noopener noreferrer" className="block relative h-[300px] md:h-[400px] overflow-hidden">
            <iframe src={site.map_embed_src} title="Map" className="w-full h-full border-0 pointer-events-none" loading="lazy" />
            <div className="absolute bottom-4 right-4 bg-white px-4 py-2 inline-flex items-center gap-2 text-xs uppercase tracking-wider shadow-md">
              <MapPin size={12} /> {lang === "tr" ? "Yol Tarifi Al" : "Get Directions"}
            </div>
          </a>
        )}
      </section>

      {site?.instagram_url && (
        <section className="py-12 md:py-16 px-6 lg:px-16 bg-[#0A0A0A] text-white overflow-hidden">
          <a
            href={site.instagram_url}
            target="_blank"
            rel="noopener noreferrer"
            className="max-w-[1440px] mx-auto flex items-center justify-center gap-3 hover:gap-5 transition-all break-all text-center"
          >
            <Instagram size={20} className="shrink-0" />
            <span className="text-xs sm:text-sm uppercase tracking-[0.18em] sm:tracking-[0.25em] md:tracking-[0.3em] max-w-full truncate sm:whitespace-normal">
              @{sanitizeInstagramHandle(hp?.instagram_handle) || "uzungolkuyumculuk"}
            </span>
          </a>
        </section>
      )}
    </div>
  );
}

// Defensive sanitiser — admin may paste a full URL into instagram_handle by mistake.
// Strip protocol, www, host, path slashes and any '@'.
function sanitizeInstagramHandle(input) {
  if (!input || typeof input !== "string") return "";
  let s = input.trim();
  if (!s) return "";
  // Strip protocol + host
  s = s.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/^instagram\.com\//i, "");
  // Take the first path segment only (no trailing slash, no query)
  s = s.split(/[/?#]/)[0];
  // Strip leading '@'
  s = s.replace(/^@+/, "");
  return s;
}

// ============ Card components ============

function CardGrid({ children }) {
  // grid-auto-rows-fr keeps every row equal — so cards stay tall-equal regardless of content.
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8 auto-rows-fr" data-testid="cms-cards-grid">
      {children}
    </div>
  );
}

function CardGridSkeleton() {
  return (
    <div data-testid="cards-skeleton" className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8 auto-rows-fr" aria-hidden="true">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className={"bg-[#EFEAE0] " + (i === 0 ? "md:col-span-2 md:row-span-2 aspect-[4/5] md:aspect-auto" : "aspect-[4/5]")} />
      ))}
    </div>
  );
}

function CardImage({ src, alt }) {
  if (!src) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] via-[#222] to-[#0A0A0A]">
        <ImageIcon className="text-white/20" size={48} strokeWidth={1} />
      </div>
    );
  }
  return (
    <OptImage
      src={src}
      alt={alt || ""}
      sizes="(min-width: 1024px) 33vw, 50vw"
      wrapperClassName="absolute inset-0 block"
      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
    />
  );
}

function CardOverlay({ title, subtitle }) {
  return (
    <>
      {/* Base gradient — always visible for legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent pointer-events-none" />
      {/* Hover lift — darkens further on hover */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500 pointer-events-none" />
      <div className="absolute bottom-5 md:bottom-7 left-5 md:left-7 right-5 md:right-7 transform transition-transform duration-500 ease-out group-hover:-translate-y-1.5">
        <h3 className="font-heading text-xl md:text-2xl lg:text-3xl text-white leading-tight">{title}</h3>
        {subtitle && (
          <span className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-white/80 mt-2 inline-flex items-center gap-2">
            {subtitle}
            <span className="inline-block h-px w-6 bg-[#C8A96A] transition-all duration-500 group-hover:w-10" />
          </span>
        )}
      </div>
    </>
  );
}

function CmsCard({ card }) {
  const href = (() => {
    if (card.link_type === "category" && card.link_target) return `/products?category=${encodeURIComponent(card.link_target)}`;
    if (card.link_type === "product" && card.link_target) return `/products/${card.link_target}`;
    if (card.link_type === "custom" && card.link_target) return card.link_target;
    return "/products";
  })();
  const isExternal = href.startsWith("http");
  const isLarge = card.size === "large";
  const className = "group block relative overflow-hidden bg-[#0A0A0A] " + (isLarge ? "md:col-span-2 md:row-span-2 aspect-[4/5] md:aspect-auto" : "aspect-[4/5]");
  const inner = (
    <>
      <CardImage src={card.image_url} alt={card.title} />
      <CardOverlay title={card.title} subtitle={card.subtitle} />
    </>
  );
  if (isExternal) {
    return <a data-testid={`cms-card-${card.id}`} href={href} target="_blank" rel="noopener noreferrer" className={className}>{inner}</a>;
  }
  return <Link data-testid={`cms-card-${card.id}`} to={href} className={className}>{inner}</Link>;
}
